/**
 * Module-local utility helpers for the eSIM feature.
 */
import { parsePhoneNumber } from 'libphonenumber-js/mobile';

/**
 * Format a fare number into a localised currency string.
 */
export const formatPrice = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return '';
  }
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch (e) {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
};

/**
 * Convert any date-ish value to yyyy-mm-dd, the shape required by the
 * PERFORMESIMOPERATION payload (`travelstartdate`, `travelenddate`, `paxdob`).
 */
export const toEsimDateString = (date) => {
  if (!date) return '';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch (e) {
    return '';
  }
};

/**
 * Whole-year age (as of today) from a DOB.
 */
export const calculateAge = (dob) => {
  if (!dob) return '';
  try {
    const d = dob instanceof Date ? dob : new Date(dob);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
    return age;
  } catch (e) {
    return '';
  }
};

export const isNullOrEmpty = (value) => {
  if (value === '' || value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
};

export const isValidEmail = (email) => {
  if (isNullOrEmpty(email)) return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).trim());
};

export const isValidMobileNumber = (mobileNumber, countryCode = 'IN') => {
  try {
    if (isNullOrEmpty(mobileNumber)) return false;
    const cleaned = String(mobileNumber).trim();
    if (!/^\d+$/.test(cleaned)) return false;
    const parsed = parsePhoneNumber(cleaned, countryCode);
    return parsed.isValid() && parsed.country === countryCode;
  } catch (error) {
    return false;
  }
};

/**
 * Passport numbers — keep the rule lenient: 6-12 alphanumeric characters,
 * since the format varies by country.
 */
export const isValidPassportNumber = (value) => {
  if (isNullOrEmpty(value)) return false;
  const cleaned = String(value).trim().toUpperCase();
  return /^[A-Z0-9]{6,12}$/.test(cleaned);
};

/**
 * Serialise the search form state into a URL query string.
 */
export const buildEsimSearchQuery = (state) => {
  const params = new URLSearchParams();
  if (state.country) params.set('country', state.country);
  if (state.travelStart) params.set('travelStart', state.travelStart);
  if (state.travelEnd) params.set('travelEnd', state.travelEnd);
  if (state.nationality) params.set('nationality', state.nationality);
  return params.toString();
};

export const parseEsimSearchQuery = (searchParams) => ({
  country: searchParams.get('country') || '',
  travelStart: searchParams.get('travelStart') || '',
  travelEnd: searchParams.get('travelEnd') || '',
  nationality: searchParams.get('nationality') || 'IN',
});

/**
 * Convert "5.0" + "GB" into a single sortable number of GB.
 *  - 0 capacity is sentinelled to a high value so that "Unlimited" plans
 *    (commonly modelled as 0) sort high. We expose a separate helper for
 *    "is unlimited" so callers can override.
 */
export const dataCapacityInGb = (plan) => {
  const cap = Number(plan?.dataCapacity || 0);
  const unit = String(plan?.dataCapacityUnit || 'GB').toUpperCase();
  if (!cap) return 0;
  if (unit === 'TB') return cap * 1024;
  if (unit === 'MB') return cap / 1024;
  return cap; // GB
};

export const isUnlimitedPlan = (plan) => {
  const text = String(plan?.planName || '').toLowerCase();
  return text.includes('unlimited');
};

export const formatDataCapacity = (plan) => {
  if (!plan) return '';
  if (isUnlimitedPlan(plan)) return 'Unlimited Data';
  const cap = Number(plan?.dataCapacity || 0);
  const unit = plan?.dataCapacityUnit || 'GB';
  if (!cap) return 'Data plan';
  return `${cap} ${unit}`;
};

export const formatValidity = (plan) => {
  if (!plan) return '';
  const validity = Number(plan?.validity || 0);
  if (!validity) return '';
  return `${validity} ${validity === 1 ? 'Day' : 'Days'}`;
};

/**
 * Turn the comma+space separated `coverages` string into a clean array.
 */
export const parseCoverages = (coverages) => {
  if (!coverages || typeof coverages !== 'string') return [];
  return coverages
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
};

/**
 * The plan description in the eSIM payload sometimes carries inline CSS / HTML
 * markup (used for the admin preview). Strip it down to readable text so we can
 * surface a clean description in the UI.
 */
export const stripPlanDescription = (description) => {
  if (!description || typeof description !== 'string') return '';
  // Remove CSS rule blocks (anything inside { ... })
  let cleaned = description.replace(/[a-zA-Z0-9-_:.#@*\s>~]+\{[^}]*\}/g, ' ');
  // Drop leftover style declarations.
  cleaned = cleaned.replace(/--[a-z-]+\s*:\s*[^;]+;/g, ' ');
  // Strip HTML tags.
  cleaned = cleaned.replace(/<[^>]+>/g, ' ');
  // Decode common HTML entities.
  cleaned = cleaned
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
  // Collapse whitespace + escaped newlines.
  cleaned = cleaned.replace(/\\n/g, '\n').replace(/\u003d/g, '=');
  cleaned = cleaned.replace(/[ \t\f\v]+/g, ' ').replace(/\n{2,}/g, '\n');
  return cleaned.trim();
};

/**
 * Derive a clean "Plan title" from the planName / genericProductName fields.
 */
export const derivePlanTitle = (plan) => {
  if (!plan) return '';
  if (plan.genericProductName) return plan.genericProductName;
  // planName layout: "USA eSIM - 5 GB - 28 Days / Data Only"
  const name = String(plan.planName || '');
  return name.split(' - ')[0] || name;
};

export const derivePlanSubtitle = (plan) => {
  if (!plan) return '';
  if (plan.genericVariantName) return plan.genericVariantName;
  const name = String(plan.planName || '');
  const parts = name.split(' - ');
  return parts.slice(1).join(' - ');
};

/**
 * Compute the trip length (whole days, inclusive of start) between two ISO
 * yyyy-mm-dd strings. Returns 0 when either value is empty.
 */
export const tripDurationInDays = (start, end) => {
  if (!start || !end) return 0;
  try {
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
    const diff = Math.round((e - s) / (1000 * 60 * 60 * 24));
    return diff + 1;
  } catch (err) {
    return 0;
  }
};

/**
 * Fallback display name for a country code (string only, no api lookup).
 */
export const titleCase = (str) => {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
};
